import * as Sentry from '@sentry/browser';
import { getExperimentFeatures } from '@toloka-tb/request';
import { type IntlShape } from 'react-intl';

import {
    type CompilationHook,
    type Core2TbType,
    type Tb2CoreType,
    type TbConfig,
    type TbContext,
    type TbJsonConfig
} from './domain';
import { loadLibScripts } from './loadAsset/loadLibScripts';
import { getAllEditors, getLock } from './providers/provider.registry/editors';
import { makeDefaultOptions, type RegistryProviderOptions } from './providers/provider.registry/options';
import { getComponents } from './providers/provider.registry/provider.registry';
import { type Intl, type Lock, type Translations } from './providers/providerDomain';
import { extractRelevantTranslations } from './providers/utils/extractRelevantTranslations';

// we create default options once so caches would persist
const defaultOptions = makeDefaultOptions();
let providerOptions: RegistryProviderOptions = defaultOptions;

export const setProviderOptions = (newOptions: Partial<RegistryProviderOptions>) => {
    providerOptions = { ...defaultOptions, ...newOptions };
};

export const preFetch = ({ lock }: { lock: Lock }) => {
    getComponents(lock, providerOptions);
};

// old core compat
const registered = new Set<string>();

export const compileConfig = async ({
    lock,
    config,
    envApi,
    hooks
}: {
    lock: Lock;
    config: TbJsonConfig;
    envApi: object;
    // @internal
    hooks?: CompilationHook[];
}): Promise<TbConfig> => {
    const { core, ...components } = await getComponents(lock, providerOptions);
    const jsonConfig = config as Tb2CoreType<typeof config>;

    for (const [type, component] of Object.entries(components)) {
        if (registered.has(type) || !component || !component.create) {
            continue;
        }

        const compiler = component.create(core, { env: envApi });

        core.register(compiler);
        registered.add(type);
    }

    const tbConfig = core.compileConfig(jsonConfig, { core, hooks });

    // ensure compatibility with old core
    tbConfig.core = core;

    return tbConfig as Core2TbType<typeof tbConfig>;
};

export const createIntl = async ({
    locales,
    configTranslations
}: {
    locales: string[];
    configTranslations: Translations;
}): Promise<IntlShape> => {
    await loadLibScripts();

    const { getIntl } = await import('./providers/provider.registry/translations');

    return getIntl(locales, configTranslations);
};

export const createContext = ({
    tbConfig,
    input,
    output,
    intl
}: {
    tbConfig: TbConfig;
    input: object;
    output?: object;
    intl: Intl;
}): TbContext => {
    const config = tbConfig as Tb2CoreType<typeof tbConfig>;
    const core = config.core!;

    const ctx = core.makeCtxV2 ? core.makeCtxV2(config, input, intl, output) : core.makeCtx(config, input, output);

    // ensure compatibility with old core
    ctx.Component = config.core!.Tb;

    return ctx as unknown as Core2TbType<typeof ctx>;
};

export const loadLibs = async () => {
    await loadLibScripts();
};

export const loadAllEditors = () => getAllEditors(providerOptions);
export const createLock = async ({ config }: { config: TbJsonConfig }) => getLock(config, providerOptions.metaCache);

export { extractRelevantTranslations };

export const initSentry = () => {
    Sentry.init({
        dsn: 'https://40fd9e7aa6f053caa0b45849312ed600@o4504211537854464.ingest.us.sentry.io/4506979641065472',
        environment: process.env.TB_LIBS === 'production' ? 'production' : 'development',
        enabled: process.env.TB_LIBS === 'production',
        enableTracing: false,
        beforeSend: (event) => {
            const exceptions = event?.exception?.values || [];

            const ignorableEvent = exceptions.some((exceptions) => {
                const { type, value } = exceptions;

                // Ignore config compilation error while build config
                if (
                    type === 'Error' &&
                    (value?.startsWith('No compiler for type') ||
                        value?.startsWith('Attempt to use recursive ref') ||
                        value?.startsWith('Attempt to compile nonexistent ref'))
                ) {
                    return true;
                }

                if (type === 'TB_WS_SDK_VALIDATION_MISMATCH') return true;

                if (
                    (type === 'MeasurementStorageError' || type === 'StorageError') &&
                    value?.includes('exceeded the quota')
                )
                    return true;

                if (value?.includes('ListError [SuggestWidget]')) return true;

                if (value?.includes('Minified Lexical error')) return true;

                return false;
            });

            return ignorableEvent ? null : event;
        }
    });
};

export const loadExperiments = async () => {
    let features: Record<string, unknown> = {};

    try {
        features = await getExperimentFeatures();
    } catch (e) {
        console.log(e);
        console.error('Can not to load experiments');
    }

    window.TB_EXPERIMENT_FEATURES = features;
};
